'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import SamanthaOrb from './samantha-orb'
import CinematicText from './cinematic-text'
import TypingDots from './typing-dots'

type SamanthaRevealProps = {
  open: boolean
  theme?: 'light' | 'dark'
  userName?: string | null
  bonusCredits?: number
  onClose: () => void
  onActivateAudio?: () => void
  onContinueWithoutAudio?: () => void
  onClaimBonus?: () => Promise<void> | void
  onOpenChat?: () => void
}

type Scene =
  | 'boot'
  | 'boot-ready'
  | 'audio-choice'
  | 'hello'
  | 'hello-name'
  | 'fade'
  | 'intro'
  | 'discover'
  | 'demo'
  | 'ready'
  | 'gift'
  | 'final'
  | 'closing'

const BOOT_LINES = [
  'Despertando núcleo conversacional...',
  'Sincronizando memoria activa...',
  'Leyendo contexto del entorno...',
  'Conectando sistema de voz...',
  'Preparando inteligencia operativa...',
  'Estabilizando presencia...',
]

const USE_STATIC_NAME_AUDIO = false
const ENABLE_TYPING_SFX = false

const agiBoot = new Audio('/sfx/agi-boot.mp3')

type SamanthaIntent = 'launch' | 'growth' | 'learn' | 'explore'

type AudioKey =
  | 'hello'
  | 'name'
  | 'intro'
  | 'discover'
  | 'intentLaunch'
  | 'intentGrowth'
  | 'intentLearn'
  | 'intentExplore'
  | 'gift'
  | 'final'

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function useTypewriterText(text: string, active: boolean, speed = 32) {
  const [output, setOutput] = useState('')

  useEffect(() => {
    if (!active) {
      setOutput('')
      return
    }

    let i = 0
    setOutput('')

    const timer = setInterval(() => {
      i += 1
      setOutput(text.slice(0, i))
      if (i >= text.length) clearInterval(timer)
    }, speed)

    return () => clearInterval(timer)
  }, [text, active, speed])

  return output
}

function BootSequence({
  palette,
  ready,
}: {
  palette: { text: string; muted: string }
  ready: boolean
}) {
  const [visibleCount, setVisibleCount] = useState(0)

  useEffect(() => {
    if (ready) return

    let i = 0
    const interval = setInterval(() => {
      i += 1
      setVisibleCount(Math.min(i, BOOT_LINES.length))
      if (i >= BOOT_LINES.length) clearInterval(interval)
    }, 650)

    return () => clearInterval(interval)
  }, [ready])

  return (
    <div className="w-full max-w-3xl text-center">
      <AnimatePresence mode="wait">
        {!ready ? (
          <motion.div
            key="boot-sequence"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2 }}
            className="space-y-6"
          >
            <div className="space-y-3">
              <div
                className="text-sm uppercase tracking-[0.28em]"
                style={{ color: palette.muted }}
              >
                Activando a Samantha
              </div>

              <div
                className="text-2xl font-semibold md:text-4xl"
                style={{ color: palette.text }}
              >
                Tu nuevo Sistema de Inteligencia
              </div>
            </div>

            <div className="mx-auto max-w-xl space-y-3 text-left">
              {BOOT_LINES.map((line, i) => (
                <motion.div
                  key={line}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: i < visibleCount ? 1 : 0 }}
                  transition={{ duration: 0.45 }}
                  className="flex items-center gap-3"
                  style={{ color: i < visibleCount ? palette.text : 'transparent' }}
                >
                  <motion.div
                    className="h-2 w-2 rounded-full bg-white"
                    animate={i === visibleCount - 1 ? { opacity: [0.25, 1, 0.25] } : { opacity: 0.55 }}
                    transition={{
                      duration: 0.9,
                      repeat: i === visibleCount - 1 ? Infinity : 0,
                    }}
                  />
                  <span className="text-sm md:text-base">{line}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="boot-ready"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.4 }}
            className="space-y-4"
          >
            <div
              className="text-4xl font-semibold tracking-tight md:text-6xl"
              style={{ color: palette.text }}
            >
              Samantha Activada.
            </div>

            <div
              className="text-base md:text-lg"
              style={{ color: palette.muted }}
            >
              El sistema está listo.
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function DiscoverOptions({
  palette,
  onSelect,
}: {
  palette: { text: string; muted: string }
  onSelect: (intent: SamanthaIntent) => void
}) {
  const items: Array<{ id: SamanthaIntent; label: string }> = [
    { id: 'launch', label: 'Lanzar una idea o producto' },
    { id: 'growth', label: 'Crear contenido para crecer mi audiencia' },
    { id: 'learn', label: 'Aprender y mejorar mis habilidades' },
    { id: 'explore', label: 'Explorar lo que Samantha puede hacer' },
  ]

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.4 }}
      className="w-full max-w-4xl space-y-8"
    >
      <div className="space-y-3 text-center">
        <h2
          className="text-4xl font-semibold tracking-tight md:text-6xl"
          style={{ color: palette.text }}
        >
          ¿Qué te gustaría lograr aquí?
        </h2>

        <p
          className="mx-auto max-w-2xl text-base md:text-lg"
          style={{ color: palette.muted }}
        >
          Elige la opción que más se acerque a lo que buscas.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => onSelect(item.id)}
            className="rounded-[28px] border px-6 py-6 text-left text-lg transition-all duration-300 hover:scale-[1.02]"
            style={{
              borderColor: 'rgba(255,255,255,0.12)',
              background: 'rgba(255,255,255,0.04)',
              color: palette.text,
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
    </motion.div>
  )
}

const INTENT_DEMO_CONTENT: Record<SamanthaIntent, { ask: string; steps: string[] }> = {
  /*
  launch: {
    ask: '“Samantha, crea un reto de 5 días para lanzar mi idea.”',
    steps: [
      'Analizando idea...',
      'Estructura del reto creada',
      'Checkpoints generados',
      'Página lista',
    ],
  },
  */
  launch: {
    ask: '“Samantha, crea un embudo para lanzar mi nuevo producto.”',
    steps: [
      "Analizando tu idea y la transformación que ofrece",
      "Diseñando la estructura del embudo de ventas",
      "Creando la página de presentación de tu producto",
      "Configurando el sistema de pagos",
      "Preparando los mensajes automáticos para nuevos clientes",
      "Todo listo para comenzar a vender"
    ],
  },


  growth: {
    ask: '“Samantha, crea una idea de contenido con hook, guion e imagen para mi próxima publicación.”',
    steps: [
      'Analizando objetivo y tipo de contenido',
      'Generando ideas y ángulos de atracción',
      'Creando hooks para captar atención',
      'Escribiendo el guion de la publicación',
      'Preparando imagen para acompañar el contenido',
      'Todo listo para publicar'
    ],
  },
  learn: {
    ask: '“Samantha, llévame a la parte exacta donde hablan de esto.”',
    steps: [
      'Analizando objetivo...',
      'Conocimiento recopilado',
      'Curso identificado',
      'Reproduciendo segmento',
    ],
  },
  explore: {
    ask: '“Samantha, muéstrame lo que puedes hacer.”',
    steps: [
      'Analizando tus ideas y objetivos',
      'Explorando oportunidades para crecer',
      'Detectando posibles estrategias',
      'Generando nuevas posibilidades',
      'Organizando las mejores opciones',
      'Listo para comenzar'
    ],
  },
}

function IntentDemo({
  palette,
  intent,
  audioEnabled,
}: {
  palette: { text: string; muted: string }
  intent: SamanthaIntent
  audioEnabled: boolean
}) {
  const example = INTENT_DEMO_CONTENT[intent]
  const [stepIndex, setStepIndex] = useState(-1)
  const [demoStarted, setDemoStarted] = useState(false)
  const [typingDone, setTypingDone] = useState(false)
  const startDelay = audioEnabled ? 7000 : 0

  const typedAsk = useTypewriterText(example.ask, demoStarted, 26)
  const typingAudioRef = useRef<HTMLAudioElement | null>(null)

  
  useEffect(() => {
    if (!ENABLE_TYPING_SFX) return

    typingAudioRef.current = new Audio('/sfx/typing.mp3')
    typingAudioRef.current.preload = 'auto'

    return () => {
      if (typingAudioRef.current) {
        typingAudioRef.current.pause()
        typingAudioRef.current.currentTime = 0
      }
    }
  }, [])  

  useEffect(() => {
    setStepIndex(-1)
    setDemoStarted(false)
    setTypingDone(false)

    let delayTimer: ReturnType<typeof setTimeout> | null = null
    let stepsTimer: ReturnType<typeof setTimeout> | null = null
    let interval: ReturnType<typeof setInterval> | null = null

    delayTimer = setTimeout(() => {
      setDemoStarted(true)

      const audio = typingAudioRef.current
      if (ENABLE_TYPING_SFX && audio) {
        audio.pause()
        audio.currentTime = 0
        audio.play().catch(() => {})
      }

      const typingMs = example.ask.length * 26 + 300

      stepsTimer = setTimeout(() => {
        setTypingDone(true)

        let current = -1
        interval = setInterval(() => {
          current += 1
          setStepIndex(current)

          if (current >= example.steps.length - 1) {
            clearInterval(interval!)
          }
        }, 1800)
      }, typingMs)
    }, startDelay)

    return () => {
      if (delayTimer) clearTimeout(delayTimer)
      if (stepsTimer) clearTimeout(stepsTimer)
      if (interval) clearInterval(interval)

      const audio = typingAudioRef.current
      if (audio) {
        audio.pause()
        audio.currentTime = 0
      }
    }
  }, [intent, audioEnabled, startDelay, example.ask, example.steps.length])

  return (
    <motion.div
      key={intent}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.4 }}
      className="max-w-3xl space-y-10"
    >
      <div
        className="min-h-[3.5rem] text-xl md:text-2xl"
        style={{ color: palette.text }}
      >
        {typedAsk}
      </div>

      <div
        className="space-y-4 text-left transition-opacity duration-700"
        style={{ opacity: typingDone ? 1 : 0 }}
      >
        {example.steps.map((step, i) => (
          <motion.div
            key={`${intent}-${i}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: i <= stepIndex ? 1 : 0.22 }}
            transition={{ duration: 0.7 }}
            className="flex items-center gap-3 text-lg"
            style={{ color: i <= stepIndex ? palette.text : palette.muted }}
          >
            <motion.div
              animate={{
                scale: i === stepIndex ? [1, 1.35, 1] : 1,
                opacity: i <= stepIndex ? (i === stepIndex ? [0.35, 1, 0.35] : 1) : 0.22,
              }}
              transition={{
                duration: 0.8,
                repeat: i === stepIndex ? Infinity : 0,
              }}
              className="h-2.5 w-2.5 rounded-full bg-white"
            />
            {step}
          </motion.div>
        ))}
      </div>
    </motion.div>
  )
}

export default function SamanthaReveal({
  open,
  theme = 'dark',
  userName,
  bonusCredits = 20,
  onClose,
  onActivateAudio,
  onContinueWithoutAudio,
  onClaimBonus,
  onOpenChat,
}: SamanthaRevealProps) {
  const [mounted, setMounted] = useState(false)
  const [scene, setScene] = useState<Scene>('boot')
  const [giftClaimed, setGiftClaimed] = useState(false)
  const [audioEnabled, setAudioEnabled] = useState(false)
  const [running, setRunning] = useState(false)
  const [selectedIntent, setSelectedIntent] = useState<SamanthaIntent | null>(null)
  const [bootReady, setBootReady] = useState(false)

  const helloNameAudioRef = useRef<HTMLAudioElement | null>(null)
  const helloNameUrlRef = useRef<string | null>(null)

  const dynamicNameAudioRef = useRef<HTMLAudioElement | null>(null)
  const dynamicNameUrlRef = useRef<string | null>(null)

  const [introPhase, setIntroPhase] = useState<1 | 2>(1)

  const hasStartedRef = useRef(false)
  const audioAllowedRef = useRef(false)
  const cancelledRef = useRef(false)

  const staticAudioRef = useRef<Record<AudioKey, HTMLAudioElement | null>>({
    hello: null,
    name: null,
    intro: null,
    discover: null,
    intentLaunch: null,
    intentGrowth: null,
    intentLearn: null,
    intentExplore: null,
    gift: null,
    final: null,
  })

  const fetchDynamicTTS = useCallback(async (text: string) => {
    try {
      const res = await fetch('/api/experience/tts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, voice: 'samantha' }),
      })

      if (!res.ok) {
        console.warn('tts failed', await res.text().catch(() => ''))
        return null
      }

      const blob = await res.blob()

      if (dynamicNameUrlRef.current) {
        URL.revokeObjectURL(dynamicNameUrlRef.current)
        dynamicNameUrlRef.current = null
      }

      const url = URL.createObjectURL(blob)
      dynamicNameUrlRef.current = url

      const audio = new Audio(url)
      audio.preload = 'auto'
      dynamicNameAudioRef.current = audio

      return audio
    } catch (err) {
      console.warn('tts error', err)
      return null
    }
  }, [])

  useEffect(() => {
    if (!open) return
    const cleanName = String(userName || '').trim()
    if (!cleanName) return

    let cancelled = false

    ;(async () => {
      try {
        const res = await fetch('/api/experience/reveal-hello-name', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: cleanName }),
        })

        if (!res.ok) return

        const blob = await res.blob()
        if (cancelled) return

        if (helloNameUrlRef.current) {
          URL.revokeObjectURL(helloNameUrlRef.current)
        }

        const url = URL.createObjectURL(blob)
        helloNameUrlRef.current = url

        const audio = new Audio(url)
        audio.preload = 'auto'
        helloNameAudioRef.current = audio
      } catch (err) {
        console.warn('preload hello-name failed', err)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [open, userName])

  useEffect(() => {
    setMounted(true)
    return () => {
      cancelledRef.current = true
    }
  }, [])

  const palette = useMemo(() => {
    const isDark = theme === 'dark'

    return {
      bg: isDark ? 'rgba(0,0,0,0.94)' : 'rgba(255,255,255,0.94)',
      text: isDark ? '#ffffff' : '#0a0a0a',
      muted: isDark ? 'rgba(255,255,255,0.68)' : 'rgba(10,10,10,0.62)',
      glow: isDark ? 'rgba(255,255,255,0.18)' : 'rgba(10,10,10,0.08)',
    }
  }, [theme])

  useEffect(() => {
    if (!mounted) return

    const files: Record<AudioKey, string> = {
      hello: '/audio/reveal/samantha/hello.mp3',
      name: '/audio/reveal/samantha/name.mp3',
      intro: '/audio/reveal/samantha/intro.mp3',
      discover: '/audio/reveal/samantha/discover.mp3',
      intentLaunch: '/audio/reveal/samantha/intent-launch.mp3',
      intentGrowth: '/audio/reveal/samantha/intent-growth.mp3',
      intentLearn: '/audio/reveal/samantha/intent-learn.mp3',
      intentExplore: '/audio/reveal/samantha/intent-explore.mp3',
      gift: '/audio/reveal/samantha/gift.mp3',
      final: '/audio/reveal/samantha/final.mp3',
    }

    const map = {} as Record<AudioKey, HTMLAudioElement>

    ;(Object.keys(files) as AudioKey[]).forEach((key) => {
      const audio = new Audio(files[key])
      audio.preload = 'auto'
      map[key] = audio
    })

    staticAudioRef.current = map

    return () => {
      Object.values(staticAudioRef.current).forEach((audio) => {
        if (!audio) return
        audio.pause()
        audio.currentTime = 0
      })

      if (dynamicNameAudioRef.current) {
        dynamicNameAudioRef.current.pause()
        dynamicNameAudioRef.current.currentTime = 0
      }

      if (dynamicNameUrlRef.current) {
        URL.revokeObjectURL(dynamicNameUrlRef.current)
        dynamicNameUrlRef.current = null
      }

      if (helloNameAudioRef.current) {
        helloNameAudioRef.current.pause()
        helloNameAudioRef.current.currentTime = 0
      }

      if (helloNameUrlRef.current) {
        URL.revokeObjectURL(helloNameUrlRef.current)
        helloNameUrlRef.current = null
      }
    }
  }, [mounted])

  const stopAllAudio = useCallback(() => {
    ;(Object.values(staticAudioRef.current) as Array<HTMLAudioElement | null>).forEach((audio) => {
      if (!audio) return
      audio.pause()
      audio.currentTime = 0
    })

    if (dynamicNameAudioRef.current) {
      dynamicNameAudioRef.current.pause()
      dynamicNameAudioRef.current.currentTime = 0
    }

    if (helloNameAudioRef.current) {
      helloNameAudioRef.current.pause()
      helloNameAudioRef.current.currentTime = 0
    }
  }, [])

  const playAudio = useCallback(async (audio: HTMLAudioElement | null) => {
    if (!audioAllowedRef.current || !audio) return

    try {
      audio.pause()
      audio.currentTime = 0
      await audio.play()

      await new Promise<void>((resolve) => {
        const onEnded = () => {
          audio.removeEventListener('ended', onEnded)
          resolve()
        }
        audio.addEventListener('ended', onEnded)
      })
    } catch (err) {
      console.warn('audio play blocked', err)
    }
  }, [])

  useEffect(() => {
    if (!open || hasStartedRef.current) return
    hasStartedRef.current = true
    cancelledRef.current = false

    const run = async () => {
      setRunning(true)
      setBootReady(false)
      setScene('boot')

      await sleep(4300)
      setBootReady(true)

      await sleep(1800)
      setScene('boot-ready')
      agiBoot.play()

      await sleep(1200)
      setScene('audio-choice')

      setRunning(false)
    }

    run()
  }, [open])

  const finishReveal = useCallback(async () => {
    setScene('final')

    if (audioAllowedRef.current) {
      await sleep(1000)
      //await playAudio(staticAudioRef.current.final)
    } else {
      await sleep(4000)
    }

    setScene('closing')
    await sleep(900)

    stopAllAudio()
    onOpenChat?.()
    onClose()
  }, [onClose, onOpenChat, playAudio, stopAllAudio])

  async function continueFlow(withAudio: boolean) {
    if (running) return

    setRunning(true)
    setSelectedIntent(null)
    setAudioEnabled(withAudio)
    audioAllowedRef.current = withAudio

    if (withAudio) onActivateAudio?.()
    else onContinueWithoutAudio?.()

    setScene('hello')

    if (withAudio) {

      const combined = USE_STATIC_NAME_AUDIO ? null : helloNameAudioRef.current

      if (combined) {
        await playAudio(combined, 4200)
        if (cancelledRef.current) return
      } else {

        await playAudio(staticAudioRef.current.hello, 1500)
        if (cancelledRef.current) return

        setScene('hello-name')

        if (USE_STATIC_NAME_AUDIO) {

          await playAudio(new Audio('/audio/reveal/samantha/name.mp3'), 2600)
          if (cancelledRef.current) return

        } else {

          const cleanName = String(userName || '').trim()

          if (cleanName) {
            const nameAudio = await fetchDynamicTTS(cleanName)
            if (cancelledRef.current) return
            await playAudio(nameAudio, 2600)
            if (cancelledRef.current) return
          } else {
            await sleep(2600)
          }

        }
      }

    } else {

      await sleep(1400)
      setScene('hello-name')
      await sleep(2600)

    }

    setScene('fade')
    await sleep(900)

    setScene('intro')
    setIntroPhase(1)

    const phaseTimer = setTimeout(() => {
      setIntroPhase(2)
    }, withAudio ? 8000 : 5000)

    if (withAudio) {
      await playAudio(staticAudioRef.current.intro, 12000)
      if (cancelledRef.current) return
    } else {
      await sleep(12000)
    }

    clearTimeout(phaseTimer)

    setScene('discover')

    if (withAudio) {
      await playAudio(staticAudioRef.current.discover)
      if (cancelledRef.current) return
    } else {
      await sleep(4000)
    }

    setRunning(false)
  }

  async function handleIntentSelect(intent: SamanthaIntent) {
    if (running) return

    setRunning(true)
    setSelectedIntent(intent)
    setScene('demo')

    if (audioAllowedRef.current) {
      if (intent === 'launch') await playAudio(staticAudioRef.current.intentLaunch)
      if (intent === 'growth') await playAudio(staticAudioRef.current.intentGrowth)
      if (intent === 'learn') await playAudio(staticAudioRef.current.intentLearn)
      if (intent === 'explore') await playAudio(staticAudioRef.current.intentExplore)

      if (cancelledRef.current) return
    } else {
      await sleep(7000)
    }

    setScene('ready')

    if (audioAllowedRef.current) {
      await playAudio(staticAudioRef.current.final, 4200)
      if (cancelledRef.current) return
    } else {
      await sleep(4200)
    }

    setScene('gift')

    try {
      await onClaimBonus?.()
      setGiftClaimed(true)
    } catch {
      setGiftClaimed(true)
    }

    if (audioAllowedRef.current) {
      await playAudio(staticAudioRef.current.gift)
    } else {
      await sleep(4500)
    }

    await finishReveal()
  }

  useEffect(() => {
    if (!open) return
    return () => {
      stopAllAudio()
      cancelledRef.current = true
    }
  }, [open, stopAllAudio])

  if (!mounted || !open) return null

  const content = (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[9999]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          background:
            theme === 'dark'
              ? 'radial-gradient(circle at center, #07070c, #000000)'
              : 'radial-gradient(circle at center, #ffffff, #f3f4f6)',
        }}
      >
        <div className="absolute inset-0 overflow-hidden">
          <SamanthaOrb />

          <div
            className="absolute inset-0 backdrop-blur-2xl"
            style={{
              background:
                theme === 'dark'
                  ? 'radial-gradient(circle at center, rgba(255,255,255,0.05), transparent 38%)'
                  : 'radial-gradient(circle at center, rgba(0,0,0,0.035), transparent 38%)',
            }}
          />

          <motion.div
            className="absolute left-1/2 top-1/2 h-[42rem] w-[42rem] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl"
            animate={{ scale: [1, 1.12, 1], opacity: [0.25, 0.65, 0.25] }}
            transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
            style={{ background: palette.glow }}
          />
        </div>

        <div className="relative flex h-full w-full items-center justify-center px-6">
          <div className="mx-auto flex w-full max-w-5xl flex-col items-center text-center">
            {(scene === 'boot' || scene === 'boot-ready') && (
              <BootSequence
                palette={{ text: palette.text, muted: palette.muted }}
                ready={bootReady}
              />
            )}

            {(scene === 'hello' || scene === 'hello-name') && (
              <div className="space-y-6">
                <div
                  className="text-5xl font-semibold md:text-7xl"
                  style={{ color: palette.text }}
                >
                  <CinematicText text={`Hola, ${String(userName || '').trim() || 'Tú'}`} />
                </div>

                <TypingDots color={palette.text} />
              </div>
            )}

            {scene === 'fade' && (
              <motion.div
                initial={{ opacity: 1 }}
                animate={{ opacity: 0 }}
                transition={{ duration: 1.2 }}
                className="absolute inset-0"
              />
            )}

            {scene === 'audio-choice' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.8 }}
                className="w-full max-w-3xl space-y-8"
              >
                <div className="space-y-4 text-center">
                  <h2
                    className="text-4xl font-semibold tracking-tight md:text-6xl"
                    style={{ color: palette.text }}
                  >
                    Vive la experiencia completa
                  </h2>

                  <p
                    className="mx-auto max-w-2xl text-base md:text-lg"
                    style={{ color: palette.muted }}
                  >
                    Activa el audio para conocer a Samantha como fue diseñada para despertar.
                  </p>
                </div>

                <div className="flex flex-col items-center justify-center gap-4 md:flex-row">
                  <Button
                    size="lg"
                    className="min-w-[220px] rounded-2xl px-8 py-6 text-base"
                    onClick={() => continueFlow(true)}
                  >
                    Activar audio
                  </Button>

                  <Button
                    size="lg"
                    variant="outline"
                    className="min-w-[220px] rounded-2xl px-8 py-6 text-base"
                    onClick={() => continueFlow(false)}
                  >
                    Continuar sin audio
                  </Button>
                </div>
              </motion.div>
            )}

            {scene === 'intro' && (
              <div className="relative min-h-[220px] w-full max-w-4xl">
                <AnimatePresence mode="wait">
                  {introPhase === 1 ? (
                    <motion.div
                      key="intro-phase-1"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.6 }}
                      className="absolute inset-0 flex flex-col items-center justify-center space-y-6 text-center"
                    >
                      <h2
                        className="text-4xl font-semibold tracking-tight md:text-6xl"
                        style={{ color: palette.text }}
                      >
                        Soy Samantha
                      </h2>

                      <p
                        className="mx-auto max-w-3xl text-lg md:text-2xl"
                        style={{ color: palette.muted }}
                      >
                        Tu nuevo sistema de inteligencia que conecta tus ideas con todo lo que necesitas para hacerlas realidad.
                      </p>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="intro-phase-2"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.6 }}
                      className="absolute inset-0 flex flex-col items-center justify-center space-y-6 text-center"
                    >
                      <p
                        className="mx-auto max-w-3xl text-lg md:text-2xl"
                        style={{ color: palette.muted }}
                      >
                        Estoy aquí para ayudarte a crear, crecer o avanzar
                        <br />
                        con lo que quieres construir.
                      </p>

                      {audioEnabled && (
                        <div
                          className="pt-2 text-sm uppercase tracking-[0.24em] hidden"
                          style={{ color: palette.muted }}
                        >
                          audio enabled
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {scene === 'discover' && (
              <DiscoverOptions
                palette={{ text: palette.text, muted: palette.muted }}
                onSelect={handleIntentSelect}
              />
            )}

            {scene === 'demo' && selectedIntent && (
              <IntentDemo
                palette={{ text: palette.text, muted: palette.muted }}
                intent={selectedIntent}
                audioEnabled={audioEnabled}
              />
            )}

            {scene === 'ready' && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.6 }}
                className="space-y-6"
              >
                <h2
                  className="text-4xl font-semibold tracking-tight md:text-6xl"
                  style={{ color: palette.text }}
                >
                  Estoy lista.
                </h2>

                <p
                  className="mx-auto max-w-2xl text-lg md:text-2xl"
                  style={{ color: palette.muted }}
                >
                  Sólo dime qué quieres lograr…
                  <br />
                  y yo me encargo de ayudarte a construirlo.
                </p>
              </motion.div>
            )}

            {scene === 'gift' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.6 }}
                className="space-y-4"
              >
                <div
                  className="text-sm uppercase tracking-[0.3em] hidden"
                  style={{ color: palette.muted }}
                >
                  A por cierto...
                </div>

                <h2
                  className="text-4xl font-semibold tracking-tight md:text-7xl"
                  style={{ color: palette.text }}
                >
                  +{bonusCredits.toLocaleString()} créditos
                </h2>

                <p
                  className="text-base md:text-lg"
                  style={{ color: palette.muted }}
                >
                  {giftClaimed ? 'Ya están disponibles en tu cuenta.' : 'Activando regalo...'}
                </p>
              </motion.div>
            )}

            {scene === 'final' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.6 }}
                className="space-y-5"
              >
                <p
                  className="mx-auto max-w-2xl text-lg md:text-2xl"
                  style={{ color: palette.muted }}
                >
                  Comencemos...
                </p>
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  )

  return createPortal(content, document.body)
}